import React from 'react';
import styled from 'styled-components';
import { GAME_CONFIG } from '../config/gameConfig';
import { RHYTHM_CONFIG } from '../config/RhythmConfig';
import { useRhythmGameLogic } from '../hooks/useRhythmGameLogic';
import { useRhythmJudgment } from '../hooks/useRhythmJudgment';
import { useRhythmInput } from '../hooks/useRhythmInput';
import { useRhythmRenderer } from '../hooks/useRhythmRenderer';
import RhythmCanvas from './RhythmCanvas';
import RhythmLanes from './RhythmLanes';
import RhythmUI from './RhythmUI';

const GameContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
`;

const GameArea = styled.div`
  width: 100%;
  height: 100%;
  max-width: 400px;
  border-radius: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;

  touch-action: none;
  overscroll-behavior: none;
  -webkit-user-select: none;
  user-select: none;
  
  @media (max-width: 768px) {
    max-width: 355px;
    max-height: ${props => props.mobileHeight || '500px'};
    margin-top: ${props => props.mobileMarginTop || '-20px'};
  }
`;

const RhythmGame = React.forwardRef(({ 
  onGameEnd, 
  currentTimeRef,
  gameMode = 'easy', 
  currentRound = 1, 
  totalRounds = 3, 
  music = RHYTHM_CONFIG.RHYTHM_MUSIC, 
  beatmap, 
  shouldPreloadAudio, 
  onAudioPreloaded, 
  audioController,
  rhythmConfig = RHYTHM_CONFIG }, ref) => {

  // 모바일 화면 크기에 따른 동적 높이 조정
  const [mobileHeight, setMobileHeight] = React.useState('500px');
  const [mobileMarginTop, setMobileMarginTop] = React.useState('-20px');

  React.useEffect(() => {
    const updateMobileDimensions = () => {
      if (window.innerWidth <= 768) {
        const screenHeight = window.innerHeight;
        const screenWidth = window.innerWidth;
        
        // 화면 크기에 따라 높이와 마진 조정
        if (screenHeight >= 800) {
          setMobileHeight('600px');
          setMobileMarginTop('-30px');
        } else if (screenHeight >= 700) {
          setMobileHeight('550px');
          setMobileMarginTop('-25px');
        } else if (screenHeight >= 600) {
          setMobileHeight('500px');
          setMobileMarginTop('-20px');
        } else {
          // 아이폰 SE 등 작은 화면용
          setMobileHeight('450px');
          setMobileMarginTop('-15px');
        }
      }
    };

    updateMobileDimensions();
    window.addEventListener('resize', updateMobileDimensions);
    window.addEventListener('orientationchange', updateMobileDimensions);

    return () => {
      window.removeEventListener('resize', updateMobileDimensions);
      window.removeEventListener('orientationchange', updateMobileDimensions);
    };
  }, []);

      // currentTimeRef를 audioController.getCurrentTime으로 설정
    React.useEffect(() => {
      if (currentTimeRef && audioController) {
        currentTimeRef.current = () => audioController.getCurrentTime();
      }
    }, [currentTimeRef, audioController]);

  // 게임 로직 훅
  const gameLogic = useRhythmGameLogic({
    onGameEnd,
    currentTimeRef,
    gameMode,
    currentRound,
    totalRounds,
    music,
    beatmap,
    shouldPreloadAudio,
    onAudioPreloaded,
    audioController,
    rhythmConfig
  });

  // 판정 시스템 훅 (임시로 startPopAnimation 없이)
  const judgment = useRhythmJudgment({
    currentTimeRef: gameLogic.currentTimeRef,
    showResult: gameLogic.showResult,
    baseScore: gameLogic.baseScore,
    hitNotesRef: gameLogic.hitNotesRef,
    missedNotesRef: gameLogic.missedNotesRef,
    comboRef: gameLogic.comboRef,
    setCombo: gameLogic.setCombo,
    setScore: gameLogic.setScore,
    setMaxCombo: gameLogic.setMaxCombo,
    perfectGroupsRef: gameLogic.perfectGroupsRef,
    groupTouchedLanesRef: gameLogic.groupTouchedLanesRef,
    groupTimeoutsRef: gameLogic.groupTimeoutsRef,
    laneQueuesRef: gameLogic.laneQueuesRef,
    activeTouchesRef: gameLogic.activeTouchesRef,
    scoreRef: gameLogic.scoreRef,
    maxComboRef: gameLogic.maxComboRef,
    PERFECT_WINDOW: gameLogic.PERFECT_WINDOW,
    GOOD_WINDOW: gameLogic.GOOD_WINDOW,
    MISS_WINDOW: gameLogic.MISS_WINDOW,
    currentBeatmap: gameLogic.currentBeatmap,
    maxScoreForRound: gameLogic.maxScoreForRound,
    groupCount: gameLogic.groupCount,
    setShowCombo: gameLogic.setShowCombo,
    rhythmConfig
  });

  // 입력 처리 훅
  const input = useRhythmInput({
    showResult: gameLogic.showResult,
    currentTimeRef: gameLogic.currentTimeRef,
    currentBeatmap: gameLogic.currentBeatmap,
    audioController,
    showJudge: judgment.showJudge,
    baseScore: gameLogic.baseScore,
    evaluateGroup: judgment.evaluateGroup,
    activeTouchesRef: gameLogic.activeTouchesRef,
    groupTouchedLanesRef: gameLogic.groupTouchedLanesRef,
    groupTimeoutsRef: gameLogic.groupTimeoutsRef,
    laneQueuesRef: gameLogic.laneQueuesRef,
    groupedNotesRef: gameLogic.groupedNotesRef,
    missedNotesRef: gameLogic.missedNotesRef,
    comboRef: gameLogic.comboRef,
    setCombo: gameLogic.setCombo,
    MISS_WINDOW: gameLogic.MISS_WINDOW,
    GOOD_WINDOW: gameLogic.GOOD_WINDOW,
    rhythmConfig
  });

  // 렌더링 훅
  const renderer = useRhythmRenderer({
    showResult: gameLogic.showResult,
    canvasRef: gameLogic.canvasRef,
    animationRef: gameLogic.animationRef,
    gameAreaRef: gameLogic.gameAreaRef,
    canvasWidth: gameLogic.canvasWidth,
    canvasHeight: gameLogic.canvasHeight,
    currentBeatmap: gameLogic.currentBeatmap,
    audioController,
    NOTE_TRAVEL_TIME: gameLogic.NOTE_TRAVEL_TIME,
    PERFECT_WINDOW: gameLogic.PERFECT_WINDOW,
    GOOD_WINDOW: gameLogic.GOOD_WINDOW,
    MISS_WINDOW: gameLogic.MISS_WINDOW,
    rhythmConfig,
    activeLanes: input.activeLanes,
    hitNotesRef: gameLogic.hitNotesRef,
    missedNotesRef: gameLogic.missedNotesRef,
    groupedNotesRef: gameLogic.groupedNotesRef,
    onGameEnd,
    scoreRef: gameLogic.scoreRef,
    maxComboRef: gameLogic.maxComboRef,
    perfectGroupsRef: gameLogic.perfectGroupsRef,
    groupCount: gameLogic.groupCount,
    maxScoreForRound: gameLogic.maxScoreForRound,
    setShowResult: gameLogic.setShowResult,
    currentTimeRef: gameLogic.currentTimeRef,
    comboRef: gameLogic.comboRef,
    evaluateGroup: judgment.evaluateGroup
  });

  // startPopAnimation을 judgment에 전달
  React.useEffect(() => {
    if (renderer.startPopAnimation) {
      judgment.setStartPopAnimation(renderer.startPopAnimation);
    }
  }, [renderer.startPopAnimation, judgment.setStartPopAnimation]);

  return (
    <GameContainer>
      <GameArea 
        ref={gameLogic.gameAreaRef}
        mobileHeight={mobileHeight}
        mobileMarginTop={mobileMarginTop}
      >
        <RhythmUI
          judgeMessage={judgment.judgeMessage}
          judgeKey={judgment.judgeKey}
          combo={gameLogic.combo}
          showCombo={gameLogic.showCombo}
          showResult={gameLogic.showResult}
          currentRound={gameLogic.currentRound}
          totalRounds={gameLogic.totalRounds}
          gameMode={gameLogic.gameMode}
          score={gameLogic.score}
          maxCombo={gameLogic.maxCombo}
        />
        {!gameLogic.showResult && (
          <RhythmCanvas
            canvasRef={gameLogic.canvasRef}
            canvasWidth={gameLogic.canvasWidth}
            canvasHeight={gameLogic.canvasHeight}
            showResult={gameLogic.showResult}
            mobileTop="80px"
            mobileHeight="calc(100% - 80px)"
          />
        )}
        <RhythmLanes
          activeLanes={input.activeLanes}
          handleLanePress={input.handleLanePress}
          handleLaneRelease={input.handleLaneRelease}
          handleLaneCancel={input.handleLaneCancel}
          rhythmConfig={rhythmConfig}
        />
      </GameArea>
    </GameContainer>
  );
});

export default RhythmGame;